/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react';
/**
 * Internal Imports
 */
import { format } from 'date-fns';
import { RootState } from '../../../redux/store';
import { CircPieChart, ChartLegend } from '../../metricsChart';
import { setComplianceReportFilter } from '../../../redux/actions';
import { logReportsClick } from '../../../utils/analyticsHelpers';
import {
  calculateTotalCompliance,
  reduceComplianceByDept
} from '../../../utils/complianceFunctions';
import { useGetEmployeeComplianceReportQuery } from '../../../redux/apis/Reports.api';
import { date as dt } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  ReducedDeptComp,
  ComplianceReport as IComplianceReport,
  Filter,
  EmpDepartment
} from '../../../config/interfaces';
/**
 * Style Imports
 */
import {
  ComplianceMetricContainer,
  TitleContainer
} from '../CustomMetricCharts.styles';

interface EmpComplianceMetricChartProps {
  facilityId: string;
  selectedEmpDepartments: EmpDepartment[];
  employeeSummaryReportFilter: Filter;
  empDepartments: EmpDepartment[];
}

const EmpComplianceMetricChart: FC<EmpComplianceMetricChartProps> = ({
  facilityId,
  selectedEmpDepartments,
  employeeSummaryReportFilter,
  empDepartments = []
}) => {
  const dispatch = useDispatch();
  const { '*': view } = useParams();
  const navigateTo = useNavigate();
  const { logTrackingEvent } = useAnalyticsApi();

  const [listToDisplayTotal, setListToDisplayTotal] =
    useState<IComplianceReport | null>(null);

  const {
    data: complianceReportData
    // isError: isGetEmployeeComplianceReportError,
    // isFetching: isGetEmployeeComplianceReportFetching,
    // isLoading: isGetEmployeeComplianceReportLoading
  } = useGetEmployeeComplianceReportQuery(
    {
      facilityId,
      startDay: (
        employeeSummaryReportFilter.startDate ?? dt.todayDateWithDashes()
      ).replace(/-/g, ''),
      endDay: dt
        .addDayToDayOfYearDate(
          employeeSummaryReportFilter.endDate ?? dt.todayDateWithDashes()
        )
        .replace(/-/g, '')
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !facilityId
    }
  );

  useEffect(() => {
    if (empDepartments.length > 0) {
      const reducedCompliance = reduceComplianceByDept(
        complianceReportData ?? [],
        empDepartments
      );

      const complianceValues: ReducedDeptComp[] =
        Object.values(reducedCompliance);
      const complianceByDeptFilter = complianceValues.filter(
        (comp: ReducedDeptComp) =>
          selectedEmpDepartments.length > 0
            ? selectedEmpDepartments.some(
                unit => unit.departmentId === comp.groupId
              )
            : comp
      );

      const cleanCompliance = complianceByDeptFilter.map(
        (item: ReducedDeptComp) => {
          return {
            department: item.groupName,
            departmentId: item.groupId,
            unitType: item.locTypeDisplay,
            total: item.combinedData.total,
            roundedPercent: item.combinedData.roundedPct,
            roundedNumber: item.combinedData.rounded,
            notRoundedPercent: item.combinedData.nonePct,
            notRoundedNumber: item.combinedData.none
          };
        }
      );

      setListToDisplayTotal(
        calculateTotalCompliance(cleanCompliance, '', '', 'employee')
      );
    }
  }, [complianceReportData, selectedEmpDepartments, empDepartments]);

  const data = useMemo(() => {
    return [
      listToDisplayTotal?.roundedNumber ?? 0,
      listToDisplayTotal?.notRoundedNumber ?? 0
    ];
  }, [listToDisplayTotal]);
  const chartLegendLabels = useMemo(() => {
    return {
      completeCount: listToDisplayTotal?.roundedNumber ?? 0,
      completePercent: listToDisplayTotal?.roundedPercent ?? 0,
      todoCount: listToDisplayTotal?.notRoundedNumber ?? 0,
      todoPercent: listToDisplayTotal?.notRoundedPercent ?? 0
    };
  }, [listToDisplayTotal]);

  const openComplianceReportTable = () => {
    dispatch(
      setComplianceReportFilter({
        ...employeeSummaryReportFilter,
        startDate:
          employeeSummaryReportFilter.startDate ??
          format(new Date(), 'yyyy-MM-dd')
      })
    );
    if (view)
      logTrackingEvent(logReportsClick(view?.replace('/', '-'), 'compliance'));
    setTimeout(() => {
      navigateTo('/reports/employee/compliance', {
        state: { from: view }
      });
    }, 1000);
  };

  return (
    <ComplianceMetricContainer
      id="Summary-Compliance-Metric-Chart"
      onClick={openComplianceReportTable}
    >
      <TitleContainer style={{ paddingLeft: '1.75rem' }}>
        Compliance
      </TitleContainer>
      <div className="d-flex flex-column px-0 mx-0">
        {/* dont draw the census metrics chart if no data */}
        {data.length > 0 && <CircPieChart data={data} />}
        {data.length > 0 && (
          <ChartLegend
            completeCount={chartLegendLabels.completeCount}
            completePercent={chartLegendLabels.completePercent}
            todoCount={chartLegendLabels.todoCount}
            todoPercent={chartLegendLabels.todoPercent}
          />
        )}
      </div>
    </ComplianceMetricContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    complianceReport: state.ReportReducer.complianceReport,
    selectedEmpDepartments: state.ConfigReducer.selectedEmpDepartments,
    employeeSummaryReportFilter:
      state.ReportReducer.employeeSummaryReportFilter,
    empDepartments: state.ConfigReducer.empDepartments
  };
};

export default connect(mapReduxStateToProps)(EmpComplianceMetricChart);
